/** Dialogs used within the checkout screen — notes collection and clinic transfer. */
import { useState } from 'react'

import type { ClinicId } from '@snootsvet/shared'

import { ClinicSearch } from '@/frontend/components/clinic-search'
import { Button } from '@/frontend/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/frontend/components/ui/dialog'
import { Textarea } from '@/frontend/components/ui/textarea'

type NotesDialogType = 'out_of_stock' | 'cancel'

const notesDialogCopy: Record<
  NotesDialogType,
  {
    title: (name: string) => string
    description: string
    confirmLabel: string
    variant: 'default' | 'destructive'
  }
> = {
  out_of_stock: {
    title: (name) => `Out of Stock — ${name}`,
    description: 'Optionally add notes for this out-of-stock item.',
    confirmLabel: 'Mark Out of Stock',
    variant: 'default',
  },
  cancel: {
    title: (name) => `Cancel — ${name}`,
    description: 'Optionally add notes for this cancellation.',
    confirmLabel: 'Cancel order',
    variant: 'destructive',
  },
}

export interface NotesDialogProps {
  open: boolean
  type: NotesDialogType
  productName: string
  /** Pre-populated value — typically the order's existing notes. */
  defaultValue?: string
  /** Whether notes are required before confirming. */
  required?: boolean
  onConfirm: (notes: string) => void
  onCancel: () => void
}

/**
 * Small modal for collecting notes before an order action (out-of-stock or cancel).
 *
 * Derives its own title, description, and button labels from the `kind` prop.
 * Uses a plain <form> with an uncontrolled textarea — the dialog unmounts on
 * close so the defaultValue resets naturally.
 */
export function NotesDialog({
  open,
  type,
  productName,
  defaultValue = '',
  required = false,
  onConfirm,
  onCancel,
}: NotesDialogProps) {
  const copy = notesDialogCopy[type]

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent className="sm:max-w-md" nested>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            const notes = new FormData(e.currentTarget).get('notes') as string
            onConfirm(notes)
          }}
        >
          <DialogHeader>
            <DialogTitle>{copy.title(productName)}</DialogTitle>
            <DialogDescription>{copy.description}</DialogDescription>
          </DialogHeader>
          <Textarea
            name="notes"
            placeholder="Notes..."
            defaultValue={defaultValue}
            required={required}
            rows={2}
            className="mt-4"
          />
          <div className="flex items-center justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" variant={copy.variant}>
              {copy.confirmLabel}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export interface TransferDialogProps {
  open: boolean
  productName: string
  onConfirm: (clinicId: ClinicId) => void
  onCancel: () => void
}

/** Small dialog for selecting a target clinic when transferring a pending order. */
export function TransferDialog({ open, productName, onConfirm, onCancel }: TransferDialogProps) {
  const [selectedClinicId, setSelectedClinicId] = useState<ClinicId | null>(null)

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          setSelectedClinicId(null)
          onCancel()
        }
      }}
    >
      <DialogContent className="sm:max-w-md" nested>
        <DialogHeader>
          <DialogTitle>Transfer — {productName}</DialogTitle>
          <DialogDescription>Select the clinic to transfer this order to.</DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <ClinicSearch
            value={selectedClinicId}
            onChange={setSelectedClinicId}
            placeholder="Select clinic..."
          />
        </div>
        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!selectedClinicId}
            onClick={() => {
              if (selectedClinicId) onConfirm(selectedClinicId)
            }}
          >
            Transfer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
